import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import styles from './MijnOutputs.module.css';
import orgStyles from './OrgOutputs.module.css';

const OUTPUT_LABELS = {
  sbar:              'SBAR',
  artsconsult:       'Artsconsult',
  rapportage:        'Verpleegkundige Rapportage',
  dienst_overdracht: 'Dienst-overdracht',
  familie:           'Familiecommunicatie',
  overplaatsing:     'Overplaatsingsoverdracht',
};

const OUTPUT_KLEUREN = {
  sbar:              { bg: '#EEEEFF', kleur: '#2D2B6B' },
  artsconsult:       { bg: '#E8F4FD', kleur: '#1a6fa8' },
  rapportage:        { bg: '#F0FBF4', kleur: '#1A6B3C' },
  dienst_overdracht: { bg: '#FFF8E6', kleur: '#B85C00' },
  familie:           { bg: '#FDF0FF', kleur: '#7B2D8B' },
  overplaatsing:     { bg: '#FFF0F0', kleur: '#CC0000' },
};

const URGENTIE_KLEUREN = {
  acuut:            { bg:'#FFF0F0', kleur:'#CC0000', label:'Acuut' },
  spoed:            { bg:'#FFF8E6', kleur:'#B85C00', label:'Spoed' },
  niet_spoedeisend: { bg:'#F0FBF4', kleur:'#1A6B3C', label:'Niet spoedeisend' },
};

const SBAR_CONFIG = {
  S: { rand:'#2D2B6B', label:'Situation',      sub:'Huidige situatie' },
  B: { rand:'#3B5FC0', label:'Background',     sub:'Achtergrond' },
  A: { rand:'#B85C00', label:'Assessment',     sub:'Beoordeling' },
  R: { rand:'#1A6B3C', label:'Recommendation', sub:'Aanbeveling' },
};

export default function OrgOutputs() {
  const { heeftPermissie } = useAuth();
  const isSuperadmin = heeftPermissie('superadmin.alles');
  const [organisaties, setOrganisaties]       = useState([]);
  const [geselecteerdeOrg, setGeselecteerdeOrg] = useState('');
  const [sessies, setSessies]             = useState([]);
  const [gebruikers, setGebruikers]       = useState([]);
  const [locaties, setLocaties]           = useState([]);
  const [afdelingen, setAfdelingen]       = useState([]);
  const [laden, setLaden]                 = useState(true);
  const [openSessieId, setOpenSessieId]   = useState(null);
  const [geselecteerde, setGeselecteerde] = useState(null);
  const [detailLaden, setDetailLaden]     = useState(false);
  const [mobieleDetailOpen, setMobieleDetailOpen] = useState(false);

  // Filters
  const [filterGebruiker, setFilterGebruiker] = useState('');
  const [filterLocatie, setFilterLocatie]     = useState('');
  const [filterAfdeling, setFilterAfdeling]   = useState('');
  const [zoekterm, setZoekterm]               = useState('');

  useEffect(() => {
    if (isSuperadmin) {
      axios.get('/api/organisaties')
        .then(r => setOrganisaties(r.data.organisaties || []))
        .catch(() => {});
    }
  }, [isSuperadmin]);

  const laadData = useCallback(async () => {
    setLaden(true);
    try {
      const params = {};
      if (isSuperadmin && geselecteerdeOrg) params.superadmin_org_id = geselecteerdeOrg;
      if (filterGebruiker) params.gebruiker_id = filterGebruiker;
      if (filterLocatie)   params.locatie_id   = filterLocatie;
      if (filterAfdeling)  params.afdeling_id  = filterAfdeling;

      const res = await axios.get('/api/outputs/organisatie', { params });
      setSessies(res.data.sessies || []);
      setGebruikers(res.data.gebruikers || []);
      setLocaties(res.data.locaties || []);
      setAfdelingen(res.data.afdelingen || []);
    } catch {
      toast.error('Organisatie outputs ophalen mislukt.');
    } finally {
      setLaden(false);
    }
  }, [filterGebruiker, filterLocatie, filterAfdeling, geselecteerdeOrg, isSuperadmin]);

  useEffect(() => { laadData(); }, [laadData]);

  async function openOutput(output) {
    setDetailLaden(true);
    setGeselecteerde(null);
    setMobieleDetailOpen(true);
    try {
      const res = await axios.get(`/api/outputs/${output.id}`);
      setGeselecteerde(res.data.output);
    } catch {
      toast.error('Output laden mislukt.');
    } finally {
      setDetailLaden(false);
    }
  }

  async function handlePdf(outputId) {
    try {
      const res = await axios.get(`/api/outputs/${outputId}/pdf`, { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = `alyden-output-${outputId}.pdf`; link.click();
    } catch { toast.error('PDF genereren mislukt.'); }
  }

  function parseInhoud(inhoud, outputType) {
    if (outputType === 'sbar') {
      try {
        const data = typeof inhoud === 'object'
          ? inhoud
          : JSON.parse(inhoud.replace(/```json\n?|\n?```/g, '').trim());
        return data;
      } catch { return null; }
    }
    return null;
  }

  function renderMarkdown(tekst) {
    if (!tekst) return null;
    const str = typeof tekst === 'string' ? tekst : JSON.stringify(tekst, null, 2);
    const cleaned = str.replace(/```[\w]*\n?|\n?```/g, '').trim();
    const lines = cleaned.split('\n');
    const elements = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) { elements.push(<br key={i} />); i++; continue; }
      if (line.trim().match(/^\*\*.*\*\*$/)) {
        elements.push(<h3 key={i} style={{fontSize:14,fontWeight:700,color:'#2D2B6B',margin:'14px 0 6px',paddingBottom:4,borderBottom:'1px solid #E0E7F0',fontFamily:'Arial'}}>{line.trim().replace(/\*\*/g,'')}</h3>);
      } else if (line.trim().match(/^[-*]\s/)) {
        const item = line.trim().replace(/^[-*]\s/,'');
        const parts = item.split(/\*\*(.*?)\*\*/g);
        elements.push(<div key={i} style={{display:'flex',gap:8,fontSize:14,color:'#1A1A2E',lineHeight:1.6,marginBottom:4,fontFamily:'Arial'}}><span style={{color:'#3B5FC0',fontWeight:700}}>•</span><span>{parts.map((p,j)=>j%2===1?<strong key={j}>{p}</strong>:p)}</span></div>);
      } else if (line.trim().match(/^\d+\.\s/)) {
        const match = line.trim().match(/^(\d+)\.\s(.*)$/);
        elements.push(<div key={i} style={{display:'flex',gap:8,fontSize:14,color:'#1A1A2E',lineHeight:1.6,marginBottom:4,fontFamily:'Arial'}}><span style={{color:'#3B5FC0',fontWeight:700,minWidth:20}}>{match[1]}.</span><span>{match[2]}</span></div>);
      } else {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        elements.push(<p key={i} style={{fontSize:14,color:'#1A1A2E',lineHeight:1.6,margin:'0 0 8px',fontFamily:'Arial'}}>{parts.map((p,j)=>j%2===1?<strong key={j}>{p}</strong>:p)}</p>);
      }
      i++;
    }
    return elements;
  }

  const gefilterd = sessies.filter(s => {
    if (!zoekterm) return true;
    const z = zoekterm.toLowerCase();
    return (s.omschrijving||'').toLowerCase().includes(z) ||
           (s.gebruiker_naam||'').toLowerCase().includes(z) ||
           (s.urgentie||'').toLowerCase().includes(z);
  });

  const gefilterdAfdelingen = afdelingen.filter(a =>
    !filterLocatie || a.locatie_id?.toString() === filterLocatie
  );

  // Superadmin zonder org selectie
  if (isSuperadmin && !geselecteerdeOrg) {
    return (
      <div className={orgStyles.pagina}>
        <div className={orgStyles.paginaHeader}>
          <div>
            <h1 className={orgStyles.titel}>Organisatie outputs</h1>
            <p className={orgStyles.subtitel}>Kies een organisatie</p>
          </div>
        </div>
        <div className={orgStyles.orgKeuze}>
          {organisaties.map(org => (
            <button key={org.id} className={orgStyles.orgKnop}
              onClick={() => setGeselecteerdeOrg(org.id.toString())}>
              <div className={orgStyles.orgBolletje}
                style={{ background: org.primaire_kleur || '#3B5FC0' }} />
              <span className={orgStyles.orgNaamTekst}>{org.naam}</span>
              <span className={orgStyles.orgPijl}>→</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={orgStyles.pagina}>
      <div className={orgStyles.paginaHeader}>
        <div>
          <h1 className={orgStyles.titel}>Organisatie outputs</h1>
          <p className={orgStyles.subtitel}>
            {isSuperadmin
              ? organisaties.find(o => o.id.toString() === geselecteerdeOrg)?.naam
              : 'Alle beoordelingen binnen uw organisatie'}
          </p>
        </div>
        {isSuperadmin && (
          <button className={orgStyles.terugKnop}
            onClick={() => { setGeselecteerdeOrg(''); setSessies([]); }}>
            ← Andere organisatie
          </button>
        )}
      </div>

      {/* Filters */}
      <div className={orgStyles.filterBalk}>
        <input type="text" className={orgStyles.zoek}
          placeholder="Zoek op omschrijving, gebruiker..."
          value={zoekterm} onChange={e => setZoekterm(e.target.value)} />

        <select value={filterGebruiker} onChange={e => setFilterGebruiker(e.target.value)}
          className={orgStyles.filterSelect}>
          <option value="">Alle gebruikers</option>
          {gebruikers.map(g => <option key={g.id} value={g.id}>{g.naam || g.email}</option>)}
        </select>

        {locaties.length > 0 && (
          <select value={filterLocatie}
            onChange={e => { setFilterLocatie(e.target.value); setFilterAfdeling(''); }}
            className={orgStyles.filterSelect}>
            <option value="">Alle locaties</option>
            {locaties.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
          </select>
        )}

        {gefilterdAfdelingen.length > 0 && (
          <select value={filterAfdeling} onChange={e => setFilterAfdeling(e.target.value)}
            className={orgStyles.filterSelect}>
            <option value="">Alle afdelingen</option>
            {gefilterdAfdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
          </select>
        )}

        {(filterGebruiker || filterLocatie || filterAfdeling || zoekterm) && (
          <button className={orgStyles.resetKnop} onClick={() => {
            setFilterGebruiker(''); setFilterLocatie('');
            setFilterAfdeling(''); setZoekterm('');
          }}>✕ Reset filters</button>
        )}
      </div>

      {mobieleDetailOpen && (
        <button className={styles.mobieleTerug}
          onClick={() => { setMobieleDetailOpen(false); setGeselecteerde(null); }}>
          ← Terug naar overzicht
        </button>
      )}

      <div className={`${styles.layout} ${mobieleDetailOpen ? styles.detailActief : ''}`}>

        {/* Sessie lijst */}
        <div className={styles.lijst}>
          {laden ? (
            <div className={styles.laadTekst}>Laden...</div>
          ) : gefilterd.length === 0 ? (
            <div className={styles.leeg}>
              <div style={{fontSize:32,marginBottom:8}}>📋</div>
              <p>Geen sessies gevonden.</p>
            </div>
          ) : (
            gefilterd.map(sessie => {
              const urg    = URGENTIE_KLEUREN[sessie.urgentie] || { bg:'#F0F4FA', kleur:'#666', label: sessie.urgentie };
              const isOpen = openSessieId === sessie.id;
              return (
                <div key={sessie.id} className={styles.sessieWrap}>
                  <div className={`${orgStyles.sessieHeaderOrg} ${isOpen ? styles.sessieOpen : ''}`}
                    onClick={() => setOpenSessieId(isOpen ? null : sessie.id)}>
                    <div className={orgStyles.sessieInhoud}>
                      <span className={styles.sessieUrgentie} style={{ background: urg.bg, color: urg.kleur }}>
                        {urg.label}
                      </span>
                      <div className={orgStyles.sessieRij2}>
                        <span className={orgStyles.sessieOmschrijvingOrg}>
                          {sessie.omschrijving || 'Geen omschrijving'}
                        </span>
                        <div className={orgStyles.sessieRechtsOrg}>
                          <span className={styles.sessieDatum}>
                            {new Date(sessie.aangemaakt_op).toLocaleDateString('nl-NL')}
                          </span>
                          <span className={styles.sessieAantal}>{sessie.outputs?.length || 0}x</span>
                          <span className={styles.sessieChevron}>{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </div>
                      <div className={orgStyles.sessieGebruikerInline}>
                        <div>👤 {sessie.gebruiker_naam || 'Onbekend'}</div>
                        {sessie.locatie_naam && (
                          <div className={orgStyles.sessieLocatieInline}>
                            📍 {sessie.locatie_naam}{sessie.afdeling_naam && ` · ${sessie.afdeling_naam}`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className={styles.sessieBody}>
                      {(sessie.outputs || []).map(output => (
                        <button key={output.id}
                          className={`${styles.outputRij} ${geselecteerde?.id === output.id ? styles.outputActief : ''}`}
                          onClick={() => openOutput(output)}>
                          <span className={styles.outputTypeBadge} style={{
                            background: OUTPUT_KLEUREN[output.output_type]?.bg || '#F0F4FA',
                            color:      OUTPUT_KLEUREN[output.output_type]?.kleur || '#1A1A2E',
                          }}>
                            {OUTPUT_LABELS[output.output_type]}
                          </span>
                          <span className={styles.outputDatum}>
                            {new Date(output.aangemaakt_op).toLocaleTimeString('nl-NL', { hour:'2-digit', minute:'2-digit' })}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Detail view */}
        <div className={styles.detail}>
          {detailLaden && <div className={styles.laadTekst}>Laden...</div>}
          {!detailLaden && !geselecteerde && (
            <div className={styles.detailLeeg}>
              <div style={{fontSize:32,marginBottom:8}}>📄</div>
              <p>Selecteer een output om te bekijken.</p>
            </div>
          )}
          {!detailLaden && geselecteerde && (() => {
            const sbarData = parseInhoud(geselecteerde.inhoud, geselecteerde.output_type);
            return (
              <div className={styles.detailInhoud}>
                <div className={styles.detailHeader}>
                  <h2 className={styles.detailTitel}
                    style={{ color: OUTPUT_KLEUREN[geselecteerde.output_type]?.kleur || '#2D2B6B' }}>
                    {OUTPUT_LABELS[geselecteerde.output_type]}
                  </h2>
                  <div className={styles.detailActies}>
                    <button className={styles.knopActie} onClick={async () => {
                      const LABELS = { S:'Situation', B:'Background', A:'Assessment', R:'Recommendation' };
                      let tekstPlain, tekstHtml;
                      try {
                        if (geselecteerde.output_type === 'sbar') {
                          const data = typeof geselecteerde.inhoud === 'object'
                            ? geselecteerde.inhoud
                            : JSON.parse(geselecteerde.inhoud.replace(/```json\n?|\n?```/g,'').trim());
                          tekstPlain = Object.entries(data).map(([k,v])=>`${k} — ${LABELS[k]||k}:\n${v}`).join('\n\n');
                          tekstHtml  = Object.entries(data).map(([k,v])=>`<h3 style="color:#2D2B6B">${k} — ${LABELS[k]||k}</h3><p>${v}</p>`).join('');
                        } else {
                          const str = typeof geselecteerde.inhoud === 'string' ? geselecteerde.inhoud : JSON.stringify(geselecteerde.inhoud, null, 2);
                          tekstPlain = str.replace(/```[\w]*\n?|\n?```/g,'').replace(/\*\*/g,'').trim();
                          tekstHtml  = str.replace(/```[\w]*\n?|\n?```/g,'').trim().split('\n').map(line => {
                            if (!line.trim()) return '<br>';
                            if (line.trim().match(/^\*\*.*\*\*$/)) return `<h3 style="color:#2D2B6B;margin:12px 0 4px">${line.trim().replace(/\*\*/g,'')}</h3>`;
                            if (line.trim().match(/^[-*]\s/)) return `<p style="margin:2px 0 2px 16px">• ${line.trim().replace(/^[-*]\s/,'').replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>')}</p>`;
                            if (line.trim().match(/^\d+\.\s/)) return `<p style="margin:2px 0 2px 16px">${line.trim()}</p>`;
                            return `<p style="margin:0 0 6px">${line.replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>')}</p>`;
                          }).join('');
                        }
                      } catch {
                        tekstPlain = typeof geselecteerde.inhoud === 'string' ? geselecteerde.inhoud : JSON.stringify(geselecteerde.inhoud, null, 2);
                        tekstHtml  = `<p>${tekstPlain}</p>`;
                      }
                      try {
                        await navigator.clipboard.write([new ClipboardItem({'text/plain':new Blob([tekstPlain],{type:'text/plain'}),'text/html':new Blob([tekstHtml],{type:'text/html'})})]);
                      } catch { navigator.clipboard.writeText(tekstPlain); }
                      toast.success('Gekopieerd.');
                    }}>📋 Kopiëren</button>
                    <button className={styles.knopActie} onClick={() => handlePdf(geselecteerde.id)}>
                      📄 PDF
                    </button>
                  </div>
                </div>
                {sbarData ? (
                  ['S','B','A','R'].map(letter => {
                    if (!sbarData[letter]) return null;
                    const cfg = SBAR_CONFIG[letter];
                    return (
                      <div key={letter} className={styles.sbarKaart}
                        style={{ borderLeftColor: cfg.rand, background: `${cfg.rand}11` }}>
                        <div className={styles.sbarBadge} style={{ background: cfg.rand }}>{letter}</div>
                        <div className={styles.sbarContent}>
                          <div className={styles.sbarLabel}>{cfg.label} — {cfg.sub}</div>
                          <p className={styles.sbarTekst}>{sbarData[letter]}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div>
                    {renderMarkdown(geselecteerde.inhoud)}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
